/**
 * Billing API Service
 * 
 * Handles billing and subscription API calls.
 */

import { apiClient } from "./apiClient";

export interface PlanPrice {
  monthly: number | null;
  yearly: number | null;
}

export interface PlanLimit {
  daily_trade_cap: number;
}

export interface Plan {
  id: string;
  code: string;
  name: string;
  prices: PlanPrice;
  limits: PlanLimit;
}

export interface CryptoAsset {
  id: string;
  symbol: string;
  network: string;
  decimals: number;
}

export interface PaymentMethods {
  crypto: {
    enabled: boolean;
    assets: CryptoAsset[];
  };
  visa: {
    enabled: boolean;
  };
}

export interface PlansResponse {
  plans: Plan[];
  payment_methods: PaymentMethods;
}

export interface SubscribeRequest {
  plan_code: string;
  period: "monthly" | "yearly";
  method: "crypto" | "visa";
  asset_id?: string;
}

export interface PaymentIntent {
  id: string;
  amount_usd: number;
  expires_at: string;
  status: string;
  wallet_address?: string;
  network?: string;
  token_contract?: string;
  expected_amount?: string;
  instructions?: string;
  reason?: string;
}

export interface SubscribeResponse {
  intent_id: string;
  wallet_address?: string;
  network?: string;
  token_contract?: string;
  expected_amount?: string;
  expires_at: string;
  instructions?: string;
}

/**
 * Get all plans with prices and payment methods.
 * Requires authentication.
 */
export async function getPlans(): Promise<PlansResponse> {
  const response = await apiClient.get<PlansResponse>("/billing/plans");
  return response.data;
}

/**
 * Create payment intent for subscription.
 * Requires authentication.
 */
export async function createPaymentIntent(
  request: SubscribeRequest
): Promise<{ intent: PaymentIntent }> {
  const response = await apiClient.post<SubscribeResponse>(
    "/billing/subscribe",
    request
  );
  // Convert SubscribeResponse to PaymentIntent format
  return {
    intent: {
      id: response.data.intent_id,
      amount_usd: 0, // Will be fetched from intent details if needed
      expires_at: response.data.expires_at,
      status: "awaiting_payment",
      wallet_address: response.data.wallet_address,
      network: response.data.network,
      token_contract: response.data.token_contract,
      expected_amount: response.data.expected_amount,
      instructions: response.data.instructions,
    },
  };
}

/**
 * Get payment intent details.
 * Requires authentication.
 */
export async function getPaymentIntent(intentId: string): Promise<PaymentIntent> {
  const response = await apiClient.get<PaymentIntent>(
    `/billing/intents/${intentId}`
  );
  return response.data;
}

/**
 * Submit transaction hash for crypto payment.
 * Requires authentication.
 */
export async function submitTransaction(
  intentId: string,
  txHash: string
): Promise<{ success: boolean; message?: string }> {
  const response = await apiClient.post(`/billing/crypto/submit-tx`, {
    intent_id: intentId,
    tx_hash: txHash,
  });
  return response.data;
}

export interface Subscription {
  id: string;
  plan: {
    code: string;
    name: string;
  };
  status: string;
  starts_at: string;
  ends_at: string | null;
}

export interface SubscriptionResponse {
  subscription: Subscription | null;
}

/**
 * Get current subscription.
 * Requires authentication.
 */
export async function getSubscription(): Promise<SubscriptionResponse> {
  const response = await apiClient.get<SubscriptionResponse>("/billing/subscription");
  return response.data;
}

export interface PaymentHistoryItem {
  id: string;
  amount_usd: number;
  method: string;
  status: string;
  tx_hash: string | null;
  confirmed_at: string | null;
  created_at: string;
}

export interface PaymentHistoryResponse {
  payments: PaymentHistoryItem[];
}

/**
 * Get payment history.
 * Requires authentication.
 */
export async function getPaymentHistory(): Promise<PaymentHistoryResponse> {
  const response = await apiClient.get<PaymentHistoryResponse>("/billing/history");
  return response.data;
}

